import { Button, Input, AixAlertDialog } from "@aix/ui"
import { useState } from "react"
import toast from "react-hot-toast"
import { useNavigate } from "@tanstack/react-router"
import { trpc } from "../../../lib/trpc"
import type { Coach } from "../../../types/coach-os.types"
import CreateTemplateFromCoachModal from "./CreateTemplateFromCoachModal"
import { ResetPlanModal } from "./ResetPlanModal"

interface SettingsTabProps {
  coach: Coach
  refetchCoach: () => void
}

export default function SettingsTab({ coach, refetchCoach }: SettingsTabProps) {
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false)
  const [showResetPlanModal, setShowResetPlanModal] = useState(false)

  // Local state for editing
  const [name, setName] = useState(coach.name)
  const [icon, setIcon] = useState(coach.icon)
  const [description, setDescription] = useState(coach.description || "")
  const [tone, setTone] = useState<"tough-love" | "chill" | "science-y">(coach.tone)
  const [targetEndDate, setTargetEndDate] = useState(coach.targetEndDate ? String(coach.targetEndDate) : "")

  // Queries
  const { data: quarterlyPlansData } = trpc.plans.quarterlyPlans.list.useQuery({
    coachId: coach.id,
    status: "active",
    limit: 1,
  })
  const activeQuarterlyPlan = quarterlyPlansData?.quarterlyPlans?.[0]

  // Mutations
  const updateMutation = trpc.coaches.update.useMutation()
  const archiveMutation = trpc.coaches.archive.useMutation()
  const deleteMutation = trpc.coaches.delete.useMutation()

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        coachId: coach.id,
        name,
        icon,
        description,
        tone,
        targetEndDate: targetEndDate || undefined,
      })
      toast.success("Coach updated successfully")
      refetchCoach()
    } catch (error: any) {
      toast.error(`Error updating coach: ${error.message}`)
    }
  }

  const handlePause = async () => {
    try {
      await updateMutation.mutateAsync({
        coachId: coach.id,
        // Note: Status update is handled by archive endpoint
        // We need to add a separate pause/resume endpoint or use metadata
      })
      toast.success("Coach paused")
      refetchCoach()
    } catch (error: any) {
      toast.error(`Error pausing coach: ${error.message}`)
    }
  }

  const handleResume = async () => {
    try {
      await updateMutation.mutateAsync({
        coachId: coach.id,
        // Note: Need pause/resume logic
      })
      toast.success("Coach resumed")
      refetchCoach()
    } catch (error: any) {
      toast.error(`Error resuming coach: ${error.message}`)
    }
  }

  const handleArchive = async (archiveType: "completed" | "archived") => {
    try {
      await archiveMutation.mutateAsync({
        coachId: coach.id,
        status: archiveType,
        actualEndDate: new Date().toISOString().split("T")[0],
      })
      toast.success(`Coach ${archiveType} successfully`)
      setShowArchiveConfirm(false)
      // Navigate back to coach list
      navigate({
        to: "/coach-os",
        // @ts-ignore
        search: (s) => ({ ...s, tab: "coaches" }),
      })
    } catch (error: any) {
      toast.error(`Error archiving coach: ${error.message}`)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        coachId: coach.id,
      })
      toast.success("Coach deleted successfully")
      setShowDeleteConfirm(false)
      // Navigate back to coach list
      navigate({
        to: "/coach-os",
        // @ts-ignore
        search: (s) => ({ ...s, tab: "coaches" }),
      })
    } catch (error: any) {
      toast.error(`Error deleting coach: ${error.message}`)
    }
  }

  const hasChanges =
    name !== coach.name ||
    icon !== coach.icon ||
    description !== (coach.description || "") ||
    tone !== coach.tone ||
    targetEndDate !== (coach.targetEndDate || "")

  return (
    <div className="space-y-6">
      {/* Edit Coach Details */}
      <div className="bg-secondary rounded-lg p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Coach Settings</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Coach Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Half Marathon Training"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Icon (Emoji)</label>
            <Input type="text" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🏃" maxLength={4} />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of this coach's purpose"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Coach Tone</label>
            <div className="grid grid-cols-3 gap-3">
              {(["tough-love", "chill", "science-y"] as const).map((toneOption) => (
                <button
                  key={toneOption}
                  onClick={() => setTone(toneOption)}
                  className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                    tone === toneOption
                      ? "border-primary bg-primary/10 text-primary font-semibold"
                      : "border-border bg-background text-foreground hover:border-primary/50"
                  }`}
                >
                  {toneOption === "tough-love" && "💪 Tough Love"}
                  {toneOption === "chill" && "😎 Chill"}
                  {toneOption === "science-y" && "🔬 Science-y"}
                </button>
              ))}
            </div>
          </div>

          {coach.type === "side-quest" && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Target End Date</label>
              <Input type="date" value={targetEndDate} onChange={(e) => setTargetEndDate(e.target.value)} />
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={handleSave} loading={updateMutation.isLoading} disabled={!hasChanges}>
              Save Changes
            </Button>
            {hasChanges && (
              <Button
                variant="outline"
                onClick={() => {
                  setName(coach.name)
                  setIcon(coach.icon)
                  setDescription(coach.description || "")
                  setTone(coach.tone)
                  setTargetEndDate(coach.targetEndDate ? String(coach.targetEndDate) : "")
                }}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Status Actions */}
      <div className="bg-secondary rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Status Management</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-background rounded-lg">
            <div>
              <p className="font-medium text-foreground">Current Status</p>
              <p className="text-sm text-muted-foreground capitalize">{coach.status}</p>
            </div>
            <div className="flex gap-2">
              {coach.status === "active" && (
                <Button variant="outline" onClick={handlePause}>
                  Pause Coach
                </Button>
              )}
              {coach.status === "paused" && <Button onClick={handleResume}>Resume Coach</Button>}
            </div>
          </div>

          {(coach.status === "active" || coach.status === "paused") && (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowArchiveConfirm(true)} className="flex-1">
                Archive Coach
              </Button>
              {coach.type === "side-quest" && (
                <Button
                  onClick={() => handleArchive("completed")}
                  loading={archiveMutation.isLoading}
                  className="flex-1"
                >
                  Mark as Completed
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reset Plan Section */}
      {activeQuarterlyPlan && (
        <div className="bg-secondary rounded-lg p-6 border-2 border-primary/20">
          <h3 className="text-lg font-semibold text-foreground mb-2">Reset Plan</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Currently on Week {activeQuarterlyPlan.currentWeek} of {activeQuarterlyPlan.durationWeeks}. If you&apos;ve
            fallen off track or your goals have changed, you can reset your plan.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowResetPlanModal(true)}
              className="border-2 border-primary text-primary hover:bg-primary/10"
            >
              🔄 Reset Plan
            </Button>
          </div>
        </div>
      )}

      {/* Template Creation */}
      <div className="bg-secondary rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Create Template</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Convert this coach into a reusable template. The template will include all the coach&apos;s configuration
          (goals, trackables, intake questions, etc.) so you can easily create similar coaches in the future.
        </p>
        <Button
          variant="outline"
          onClick={() => setShowCreateTemplateModal(true)}
          className="border-2 border-primary text-primary hover:bg-primary/10"
        >
          📦 Create Template from This Coach
        </Button>
      </div>

      {/* Danger Zone */}
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-destructive mb-2">Danger Zone</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Delete this coach and hide it from your active coaches list. The coach and all associated data can be restored
          later from the deleted coaches view.
        </p>
        <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
          Delete Coach
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AixAlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Coach?"
        description={`Are you sure you want to delete "${coach.name}"? The coach will be hidden from your active list but can be restored later if needed.`}
        actionLabel="Delete"
        cancelLabel="Cancel"
        onActionClick={handleDelete}
        actionClassName="bg-destructive text-destructive-foreground"
      />

      {/* Archive Confirmation Dialog */}
      <AixAlertDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        title="Archive Coach?"
        description={`Are you sure you want to archive "${coach.name}"? The coach will be moved to archived status and won't appear in active lists.`}
        actionLabel="Archive"
        cancelLabel="Cancel"
        onActionClick={() => handleArchive("archived")}
      />

      {/* Create Template Modal */}
      <CreateTemplateFromCoachModal
        isOpen={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        coach={coach}
      />

      {/* Reset Plan Modal */}
      {showResetPlanModal && activeQuarterlyPlan && (
        <ResetPlanModal
          coachId={coach.id}
          coachName={coach.name}
          currentWeek={activeQuarterlyPlan.currentWeek}
          onClose={() => setShowResetPlanModal(false)}
        />
      )}
    </div>
  )
}
